import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  Plus,
  Loader2,
  Send,
  User,
  Paperclip,
  File,
  Trash2,
  Download,
} from 'lucide-react';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import {
  usePortalMessages,
  useSendPortalMessage,
  useMarkPortalMessageRead,
  type PortalMessage,
} from '@/hooks/usePortalMessages';
import {
  validateMessageAttachmentFile,
  useAttachMessageDocument,
  useMessageAttachments,
} from '@/hooks/useMessageAttachments';
import { useUploadDocument, useGetDocumentDownloadUrl } from '@/hooks/useDocuments';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import { Button } from '@/components/ui/Button';

function MessageAttachments({ messageId }: { messageId: string }) {
  const { t } = useTranslation();
  const { data: attachments, isLoading } = useMessageAttachments(messageId);
  const getDownloadUrl = useGetDocumentDownloadUrl();

  const handleDownload = async (documentId: string, filename: string) => {
    try {
      const url = await getDownloadUrl.mutateAsync(documentId);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      toast.error(t('documents.errors.download_failed', 'Kunne ikke downloade fil'));
    }
  };

  if (isLoading || !attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 space-y-1">
      {attachments.map((attachment) => (
        <button
          key={attachment.id}
          onClick={() => handleDownload(attachment.document_id, attachment.document.original_filename)}
          disabled={getDownloadUrl.isPending}
          className="flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 w-full text-left"
        >
          <File className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{attachment.document.original_filename}</span>
          <Download className="h-3 w-3 flex-shrink-0 ml-auto" />
        </button>
      ))}
    </div>
  );
}

export function PortalMessages() {
  const { t } = useTranslation();
  const { activeClinicId, clinics } = usePortalActiveClinic();
  const activeClinic = clinics.find((c) => c.clinic_id === activeClinicId);
  
  const { data: messages, isLoading, error } = usePortalMessages(activeClinicId);
  const sendMessage = useSendPortalMessage();
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachMessageDocument(activeClinicId ?? undefined);
  const markRead = useMarkPortalMessageRead();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showComposer, setShowComposer] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    
    for (const file of files) {
      const validationError = validateMessageAttachmentFile(file);
      if (validationError) {
        if (validationError === 'size_limit_exceeded') {
          toast.error(t('documents.errors.size_limit', `Filen ${file.name} er for stor (max 50 MB)`));
        } else if (validationError === 'invalid_type') {
          toast.error(t('documents.errors.invalid_type', `Filtypen for ${file.name} er ikke tilladt`));
        }
      } else {
        validFiles.push(file);
      }
    }
    
    setSelectedFiles((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (!activeClinicId) {
      toast.error(t('portal.messages.error_no_clinic', 'Ingen klinik valgt'));
      return;
    }

    if (!messageContent.trim() && selectedFiles.length === 0) {
      toast.error(t('messages.error_empty', 'Besked eller fil påkrævet'));
      return;
    }

    setIsUploading(true);

    try {
      // First, send the message
      const sentMessage = await sendMessage.mutateAsync({
        clinic_id: activeClinicId,
        content: messageContent.trim() || '(Fil vedhæftet)',
      });

      // Then upload and attach files if any
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const doc = await uploadDocument.mutateAsync({
            clinicId: (sentMessage as any).clinic_id,
            patientId: (sentMessage as any).patient_id,
            file: file,
            tags: ['message'],
          });
          
          await attachDocument.mutateAsync({
            message_id: (sentMessage as any).id,
            document_id: doc.id,
          });
        }
      }

      toast.success(t('messages.sent', 'Besked sendt'));
      setMessageContent('');
      setSelectedFiles([]);
      setShowComposer(false);
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error(t('messages.error_sending', 'Kunne ikke sende besked'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleMarkAsRead = async (messageId: string) => {
    if (!activeClinicId) return;
    
    try {
      await markRead.mutateAsync({
        message_id: messageId,
        clinic_id: activeClinicId,
      });
      toast.success(t('messages.marked_read', 'Markeret som læst'));
    } catch (err) {
      console.error('Error marking message as read:', err);
      toast.error(t('messages.error_marking_read', 'Kunne ikke markere som læst'));
    }
  };

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return format(date, 'dd. MMMM yyyy HH:mm', { locale: da });
  };

  const renderMessage = (msg: PortalMessage) => {
    const isFromPatient = msg.sender === 'patient';
    const isUnread = !msg.read_at && !isFromPatient; // Unread messages from clinic

    return (
      <div
        key={msg.id}
        className={`rounded-lg border p-4 ${
          isUnread
            ? 'border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-900/20'
            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-card-dark'
        }`}
      >
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                isFromPatient
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              }`}
            >
              {isFromPatient ? (
                <User className="h-4 w-4" />
              ) : (
                <MessageSquare className="h-4 w-4" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {isFromPatient 
                  ? t('portal.messages.you', 'Dig') 
                  : activeClinic?.clinic_name ?? t('messages.from_clinic', 'Klinikken')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatDateTime(msg.created_at)}
              </p>
            </div>
          </div>
          {isUnread && (
            <button
              onClick={() => handleMarkAsRead(msg.id)}
              disabled={markRead.isPending}
              className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {t('messages.mark_read', 'Markér som læst')}
            </button>
          )}
        </div>
        <div className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
          {msg.content}
        </div>
        <MessageAttachments messageId={msg.id} />
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-200">
        {t('messages.error_loading', 'Kunne ikke indlæse beskeder')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('portal.messages.title', 'Beskeder')}
          </h1>
          {activeClinic && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {t('portal.messages.subtitle', 'Kommunikér med')} {activeClinic.clinic_name}
            </p>
          )}
        </div>
        <Button onClick={() => setShowComposer(!showComposer)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('portal.messages.new_message', 'Ny besked')}
        </Button>
      </div>

      {showComposer && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-card-dark">
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('portal.messages.compose_label', 'Skriv din besked')}
          </label>
          <textarea
            value={messageContent}
            onChange={(e) => setMessageContent(e.target.value)}
            rows={4}
            placeholder={t('portal.messages.compose_placeholder', 'Skriv din besked her...')}
            disabled={isUploading}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500"
          />

          <div className="mt-3">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            >
              <Paperclip className="h-4 w-4" />
              {t('portal.messages.attach_file', 'Vedhæft fil')}
            </button>

            {selectedFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <File className="h-4 w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                      <span className="truncate text-sm text-gray-700 dark:text-gray-200">
                        {file.name}
                      </span>
                      <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                        ({Math.round(file.size / 1024)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      disabled={isUploading}
                      className="ml-2 flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-3 flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowComposer(false);
                setMessageContent('');
                setSelectedFiles([]);
              }}
              disabled={isUploading}
            >
              {t('common.cancel', 'Annuller')}
            </Button>
            <Button
              onClick={handleSendMessage}
              disabled={isUploading || (!messageContent.trim() && selectedFiles.length === 0)}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('messages.sending', 'Sender...')}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {t('messages.send', 'Send')}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {!messages || messages.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center dark:border-gray-700 dark:bg-gray-800">
          <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('portal.messages.no_messages', 'Ingen beskeder endnu')}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            {t('portal.messages.no_messages_hint', 'Send en besked til klinikken for at starte')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map(renderMessage)}
        </div>
      )}
    </div>
  );
}
